// JSON-LD for blog posts, shared by the SPA (BlogStructuredData) and the crawler renderer
// (api/render.ts) so both always emit the same graph. Pure: no Vite, React or path aliases.

import type { BlogPostMeta } from '../content/blog/posts.js';
import { BLOG_SITE, absoluteUrl } from './blog.js';
import { nameToSlug } from './blogCategories.js';

const ORG_ID = `${BLOG_SITE}/#organization`;
const WEBSITE_ID = `${BLOG_SITE}/#website`;
export const BLOG_ID = `${BLOG_SITE}/blog#blog`;

const PUBLISHER = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'Beatview',
  url: BLOG_SITE,
  logo: { '@type': 'ImageObject', url: `${BLOG_SITE}/beatview-logo.svg` },
};

/** Visible words in an HTML fragment. */
export function countWords(html: string): number {
  return html.replace(/<[^>]*>/g, ' ').replace(/&[a-z#0-9]+;/gi, ' ').split(/\s+/).filter(Boolean).length;
}

/** Splits an article body into its main part and the trailing "Sources" section, so pages can
 * render the FAQ between them. */
export function splitSources(html: string): { main: string; sources: string } {
  const i = html.search(/<h2[^>]*>Sources<\/h2>/i);
  return i === -1 ? { main: html, sources: '' } : { main: html.slice(0, i), sources: html.slice(i) };
}

export function blogPostUrl(slug: string) {
  return `${BLOG_SITE}/blog/${slug}`;
}

export function blogPostSchemas(post: BlogPostMeta, wordCount?: number): object[] {
  const url = blogPostUrl(post.slug);
  const categorySlug = nameToSlug.get(post.category);
  const image = {
    '@type': 'ImageObject',
    url: absoluteUrl(post.cover),
    width: 1200,
    height: 630,
    caption: post.coverAlt,
  };

  const article = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    headline: post.title,
    alternativeHeadline: post.seoTitle,
    description: post.seoDescription,
    abstract: post.takeaways.join(' '),
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url, url, isPartOf: { '@id': WEBSITE_ID } },
    image,
    thumbnailUrl: absoluteUrl(post.cover),
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    author: PUBLISHER,
    publisher: PUBLISHER,
    isPartOf: { '@type': 'Blog', '@id': BLOG_ID, name: 'Beatview Blog', url: `${BLOG_SITE}/blog`, publisher: { '@id': ORG_ID } },
    articleSection: post.category,
    keywords: [post.primaryKeyword, ...post.tags.filter(t => t !== post.primaryKeyword)].join(', '),
    about: { '@type': 'Thing', name: post.primaryKeyword },
    inLanguage: 'en',
    ...(wordCount ? { wordCount } : {}),
    timeRequired: `PT${post.readingTime}M`,
    speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.blog-takeaways', '.blog-article h2'] },
  };

  const crumbs = [
    { name: 'Home', item: BLOG_SITE },
    { name: 'Blog', item: `${BLOG_SITE}/blog` },
    ...(categorySlug ? [{ name: post.category, item: `${BLOG_SITE}/blog/${categorySlug}` }] : []),
    { name: post.title, item: url },
  ];
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({ '@type': 'ListItem', position: i + 1, name: c.name, item: c.item })),
  };

  const schemas: object[] = [article, breadcrumb];
  if (post.faqs.length) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: post.faqs.map(f => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
    });
  }
  return schemas;
}
